// Super-admin topbar notification bell.
// Polls /api/user/v1/notification every 30s. Dropdown shows up to
// 10 most-recent rows with severity chip + relative time + body
// preview. Click "View all" → /super/alerts.
//
// IMPORTANT: this component is the SUPER-ADMIN surface. The tenant
// (org-user) notification bell lives in src/layouts/_new/Normal.js
// and is a separate surface — do NOT cross-wire them.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Menu,
  Stack,
  Typography,
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useNavigate } from "react-router-dom";

import { TOKENS } from "./design";
import {
  getNotifications,
  markRead,
  markAllRead,
} from "../../../../services/notificationsService";

// Backend doesn't expose a `severity` column on the response — derive
// it from the `type` enum so the chips still convey something useful.
// Anything we don't know maps to "low" / grey.
const SEVERITY_BY_TYPE = {
  ENGINE_HEALTH: "high",
  LICENSE_BREACH: "high",
  LICENSE_EXPIRED: "high",
  LICENSE_EXPIRING: "medium",
  QUOTA_BREACH: "high",
  QUOTA_REQUEST: "medium",
  POSTURE_FINDING: "medium",
  ANOMALY: "medium",
  WAF_ALERT: "medium",
  DDOS_ALERT: "high",
  BM_ALERT: "medium",
  INFO: "low",
  DEFAULT: "low",
};

export const severityFor = (type) => SEVERITY_BY_TYPE[type] || "low";

const SEVERITY_STYLE = {
  high: { ink: TOKENS.dangerInk, bg: TOKENS.dangerBg, border: TOKENS.dangerBorder, dot: TOKENS.danger },
  medium: { ink: TOKENS.warningInk, bg: TOKENS.warningBg, border: TOKENS.warningBorder, dot: TOKENS.warning },
  low: { ink: TOKENS.fg2, bg: TOKENS.bgSunken, border: TOKENS.border, dot: TOKENS.fg3 },
};

export const SeverityChip = ({ severity }) => {
  const s = SEVERITY_STYLE[severity] || SEVERITY_STYLE.low;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: 22,
        px: "8px",
        borderRadius: 999,
        background: s.bg,
        color: s.ink,
        border: `1px solid ${s.border}`,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        textTransform: "capitalize",
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {severity}
    </Box>
  );
};

export const relativeTime = (ts) => {
  if (!ts) return "";
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const POLL_MS = 30000;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Track whether the component is still mounted so the interval handler
  // doesn't setState after unmount on slow networks.
  const mountedRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotifications();
      const data = res?.data ?? res;
      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];
      if (mountedRef.current) setItems(list);
    } catch (_e) {
      // Soft-fail — bell stays at the last known count rather than blanking out.
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchOnce]);

  const unreadCount = useMemo(
    () => items.filter((n) => !n.read).length,
    [items]
  );

  const recent = useMemo(
    () =>
      items
        .slice()
        .sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0))
        .slice(0, 10),
    [items]
  );

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMarkAll = async (e) => {
    e.stopPropagation();
    // Optimistic — flip everything to read locally, then re-fetch.
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await markAllRead();
    } catch (_e) {
      /* re-fetch will correct if it failed */
    }
    fetchOnce();
  };

  const handleItemClick = async (n) => {
    handleClose();
    if (!n.read) {
      setItems((prev) =>
        prev.map((row) => (row.id === n.id ? { ...row, read: true } : row))
      );
      try {
        await markRead(n.id);
      } catch (_e) {
        /* re-fetch will correct */
      }
    }
    navigate("/super/alerts");
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="notifications"
        sx={{
          color: TOKENS.fg2,
          "&:hover": { background: TOKENS.bgSunken, color: TOKENS.fg1 },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              height: 16,
              minWidth: 16,
            },
          }}
        >
          {unreadCount > 0 ? (
            <NotificationsOutlinedIcon />
          ) : (
            <NotificationsNoneIcon />
          )}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            border: `1px solid ${TOKENS.border}`,
            boxShadow: TOKENS.shadowL,
            borderRadius: `${TOKENS.radiusS}px`,
            overflow: "hidden",
          },
        }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
            background: TOKENS.bgSurface,
          }}
        >
          <Typography sx={ { fontSize: 14, fontWeight: 600, color: TOKENS.fg1 }}>
            Recent Alerts
          </Typography>
          <Button
            size="small"
            disabled={unreadCount === 0 || loading}
            onClick={handleMarkAll}
            sx={{
              textTransform: "none",
              fontSize: 12,
              color: TOKENS.fgLink,
              minWidth: 0,
              p: 0,
              "&:hover": { background: "transparent", textDecoration: "underline" },
            }}
          >
            Mark all read
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {recent.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 5, px: 2 }}>
              <NotificationsNoneIcon sx={{ color: TOKENS.fg3, fontSize: 32 }} />
              <Typography sx={ { fontSize: 13, color: TOKENS.fg3 }}>
                No recent alerts
              </Typography>
            </Stack>
          ) : (
            recent.map((n) => {
              const sev = severityFor(n.type);
              return (
                <Box
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: `1px solid ${TOKENS.borderSubtle}`,
                    cursor: "pointer",
                    background: n.read ? "transparent" : TOKENS.primaryTint,
                    "&:hover": { background: TOKENS.bgSunken },
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <SeverityChip severity={sev} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: TOKENS.fg1,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {n.title || "Notification"}
                    </Typography>
                    <Typography sx={ { fontSize: 11, color: TOKENS.fg3, flexShrink: 0 }}>
                      {relativeTime(n.created)}
                    </Typography>
                  </Stack>
                  {n.content ? (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: TOKENS.fg2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {n.content}
                    </Typography>
                  ) : null}
                </Box>
              );
            })
          )}
        </Box>

        <Box
          onClick={() => {
            handleClose();
            navigate("/super/alerts");
          }}
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${TOKENS.borderSubtle}`,
            background: TOKENS.bgSunken,
            cursor: "pointer",
            textAlign: "center",
            "&:hover": { background: TOKENS.primaryTint },
          }}
        >
          <Typography sx={ { fontSize: 13, fontWeight: 500, color: TOKENS.fgLink }}>
            View all alerts &rarr;
          </Typography>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
